import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import sdkApi from '../utils/sdkApi';

// Create the context
const AnalyticsCacheContext = createContext();

export const AnalyticsCacheProvider = ({ children }) => {
  // Main analytics data cache
  const [analytics, setAnalytics] = useState({});
  // DEPRECATED: Overview model has been removed - overviewData will be computed from PostgreSQL sessions
  const [overviewData, setOverviewData] = useState({});
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  // DEPRECATED: Overview model has been removed
  const [isLoadingOverview, setIsLoadingOverview] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  // DEPRECATED: Overview model has been removed
  const [overviewError, setOverviewError] = useState(null);
  
  // Cache management
  const analyticsCache = useRef(new Map()); // Cache analytics data by website ID
  // DEPRECATED: Overview model has been removed
  const overviewCache = useRef(new Map()); // Cache overview data by website ID
  const retentionCache = useRef(new Map()); // Cache retention data by website ID
  const componentDataCache = useRef(new Map()); // Cache processed data for specific components
  const chartDataCache = useRef(new Map()); // Cache chart-specific data
  const lastRefreshTime = useRef(Date.now() - 60000); // Initialize to 1 minute ago
  
  // Cache configuration - Increased cache durations to reduce API calls
  const CACHE_CONFIG = {
    analytics: 15 * 60 * 1000, // 15 minutes for raw analytics data (increased from 5)
    // DEPRECATED: Overview model has been removed
    overview: 15 * 60 * 1000, // 15 minutes for overview data
    retention: 15 * 60 * 1000, // 15 minutes for retention data
    componentData: 20 * 60 * 1000, // 20 minutes for processed component data (increased from 10)
    chartData: 30 * 60 * 1000, // 30 minutes for chart data (increased from 15)
    maxCacheSize: 100 // Maximum number of cached items per cache type
  };

  // Cache utility functions
  const cleanupCache = useCallback((cache, maxSize) => {
    if (cache.size > maxSize) {
      const entries = Array.from(cache.entries());
      entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
      const toDelete = entries.slice(maxSize);
      toDelete.forEach(([key]) => cache.delete(key));
    }
  }, []);

  const setCacheItem = useCallback((cache, key, data, duration) => {
    cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + duration
    });
    cleanupCache(cache, CACHE_CONFIG.maxCacheSize);
  }, [cleanupCache]);

  const getCacheItem = useCallback((cache, key) => {
    const item = cache.get(key);
    if (!item) return null;
    
    if (Date.now() > item.expiresAt) {
      cache.delete(key);
      return null;
    }
    
    return item.data;
  }, []);

  // Function to fetch analytics data for a website
  const fetchAnalyticsData = useCallback(async (websiteId, forceFetch = false) => {
    if (!websiteId) {
      return null;
    }

    // Check cache first
    if (!forceFetch) {
      const cached = getCacheItem(analyticsCache.current, websiteId);
      if (cached) {
        setAnalytics(cached);
        return cached;
      }
    }

    setIsLoadingAnalytics(true);
    setAnalyticsError(null);

    try {
      const response = await sdkApi.getAnalytics(websiteId);

      if (response.subscriptionError) {
        console.error("Subscription error:", response.message);
        setAnalyticsError(response.message);
        return null;
      }

      if (response.error) {
        setAnalyticsError(response.message);
        return null;
      }

      if (response && response.analytics) {
        // Cache the analytics data
        setCacheItem(analyticsCache.current, websiteId, response.analytics, CACHE_CONFIG.analytics);
        setAnalytics(response.analytics);
        return response.analytics;
      }

      return null;
    } catch (error) {
      console.error("Error fetching analytics data:", error);
      setAnalyticsError("Failed to load analytics data");
      return null;
    } finally {
      setIsLoadingAnalytics(false);
    }
  }, [getCacheItem, setCacheItem]);

  // Function to fetch overview data for a website
  const fetchOverviewData = useCallback(async (websiteId, forceFetch = false) => {
    // Analytics endpoint removed - Overview model has been removed
    // Analytics data will be computed from PostgreSQL sessions table directly
    setIsLoadingOverview(false);
    setOverviewError("Overview data is temporarily unavailable. This feature will be reimplemented using PostgreSQL sessions.");
      return null;
  }, []);

  // DEPRECATED: Overview model has been removed - returns null
  // Function to get overview data (from cache or fetch new)
  const getOverviewData = useCallback(async (websiteId, forceFetch = false) => {
    // Overview model has been removed - will be reimplemented using PostgreSQL sessions
    setOverviewData({});
    return null;
  }, []);

  // Function to fetch retention data - Analytics endpoint removed
  const fetchRetentionData = useCallback(async (websiteId, cohortFilter = 'Last Month', chartFilter = 'Last Month', forceFetch = false) => {
    // Analytics endpoint removed - Overview model has been removed
    // Analytics data will be computed from PostgreSQL sessions table directly
      return null;
  }, []);

  // Function to get retention data (from cache or fetch new)
  const getRetentionData = useCallback(async (websiteId, cohortFilter = 'Last Month', chartFilter = 'Last Month', forceFetch = false) => {
    if (!websiteId) return null;

    const cacheKey = `${websiteId}-${cohortFilter}-${chartFilter}`;
    const cached = getCacheItem(retentionCache.current, cacheKey);

    // Use cached data if available and not expired
    if (!forceFetch && cached) {
      return cached;
    }

    // Fetch fresh data
    const freshData = await fetchRetentionData(websiteId, cohortFilter, chartFilter, true);
    return freshData;
  }, [fetchRetentionData, getCacheItem]);

  // Function to get analytics data (from cache or fetch new)
  const getAnalytics = useCallback(async (websiteId, forceFetch = false) => {
    if (!websiteId) return null;

    const now = Date.now();
    const cached = getCacheItem(analyticsCache.current, websiteId);

    // Use cached data if available and not expired
    if (!forceFetch && cached) {
      setAnalytics(cached);
      return cached;
    }

    // Only fetch fresh data if we don't have any cached data or if explicitly forced
    if (!cached || forceFetch) {
      const freshData = await fetchAnalyticsData(websiteId, true);
      if (freshData) {
        setAnalytics(freshData);
      }
      return freshData;
    }

    return cached;
  }, [fetchAnalyticsData, getCacheItem]);

  // DEPRECATED: Overview model has been removed
  // Function to preload all data for a website (Overview + Retention)
  const preloadAllData = useCallback(async (websiteId) => {
    if (!websiteId) return;

    // Deprecated: Overview model has been removed

    // Only preload retention data (overview is deprecated)
    try {
      const retentionResult = await Promise.allSettled([
        getRetentionData(websiteId, 'Last Month', 'Last Month', false)
      ]);

      return {
        overview: null, // DEPRECATED
        retention: retentionResult[0].status === 'fulfilled' ? retentionResult[0].value : null
      };
    } catch (error) {
      console.error('Error preloading data:', error);
      return null;
    }
  }, [getRetentionData]);

  // Function to cache component-specific processed data
  const cacheComponentData = useCallback((componentKey, data, websiteId) => {
    const cacheKey = `${websiteId}:${componentKey}`;
    setCacheItem(componentDataCache.current, cacheKey, data, CACHE_CONFIG.componentData);
    return data;
  }, [setCacheItem]);

  // Function to get cached component data
  const getCachedComponentData = useCallback((componentKey, websiteId) => {
    const cacheKey = `${websiteId}:${componentKey}`;
    return getCacheItem(componentDataCache.current, cacheKey);
  }, [getCacheItem]);

  // Function to cache chart data
  const cacheChartData = useCallback((chartKey, data, websiteId) => {
    const cacheKey = `${websiteId}:${chartKey}`;
    setCacheItem(chartDataCache.current, cacheKey, data, CACHE_CONFIG.chartData);
    return data;
  }, [setCacheItem]);

  // Function to get cached chart data
  const getCachedChartData = useCallback((chartKey, websiteId) => {
    const cacheKey = `${websiteId}:${chartKey}`;
    return getCacheItem(chartDataCache.current, cacheKey);
  }, [getCacheItem]);

  // Function to clear cache for a specific website
  const clearWebsiteCache = useCallback((websiteId) => {
    // Clear analytics cache
    analyticsCache.current.delete(websiteId);
    
    // DEPRECATED: Overview model removed - clear overview cache
    overviewCache.current.delete(websiteId);
    
    // Clear component data cache for this website
    const componentKeys = Array.from(componentDataCache.current.keys());
    componentKeys.forEach(key => {
      if (key.startsWith(`${websiteId}:`)) {
        componentDataCache.current.delete(key);
      }
    });
    
    // Clear chart data cache for this website
    const chartKeys = Array.from(chartDataCache.current.keys());
    chartKeys.forEach(key => {
      if (key.startsWith(`${websiteId}:`)) {
        chartDataCache.current.delete(key);
      }
    });
    
  }, []);

  // Function to clear all caches
  const clearAllCaches = useCallback(() => {
    analyticsCache.current.clear();
    // DEPRECATED: Overview model removed
    overviewCache.current.clear();
    componentDataCache.current.clear();
    chartDataCache.current.clear();
  }, []);

  // Function to get analytics data without triggering a fetch (for tab switching)
  const getCachedAnalyticsOnly = useCallback((websiteId) => {
    if (!websiteId) return null;
    const cached = getCacheItem(analyticsCache.current, websiteId);
    if (cached) {
      setAnalytics(cached);
      return cached;
    }
    return null;
  }, [getCacheItem]);

  // Function to get cache statistics
  const getCacheStats = useCallback(() => {
    return {
      analytics: analyticsCache.current.size,
      overview: overviewCache.current.size,
      componentData: componentDataCache.current.size,
      chartData: chartDataCache.current.size,
      total: analyticsCache.current.size + overviewCache.current.size + componentDataCache.current.size + chartDataCache.current.size
    };
  }, []);

  // Listen for website changes
  useEffect(() => {
    let currentWebsiteId = localStorage.getItem("idy");

    const checkWebsiteChange = async () => {
      const newWebsiteId = localStorage.getItem("idy");
      const now = Date.now();

      if (newWebsiteId && newWebsiteId !== currentWebsiteId) {
        currentWebsiteId = newWebsiteId;
        lastRefreshTime.current = now;
        // DEPRECATED: Overview model removed - only refresh analytics
        await getAnalytics(newWebsiteId, true);
      } else if (newWebsiteId && (now - lastRefreshTime.current > 600000)) {
        // Refresh every 10 minutes (increased from 5 minutes to reduce API calls)
        lastRefreshTime.current = now;
        // DEPRECATED: Overview model removed - only refresh analytics
        await getAnalytics(newWebsiteId);
      }
    };

    // Initial load
    if (currentWebsiteId) {
      // DEPRECATED: Overview model removed - only load analytics
      getAnalytics(currentWebsiteId);
    }

    // Check for changes every 30 seconds (reduced from 2 seconds to prevent excessive API calls)
    const intervalId = setInterval(checkWebsiteChange, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [getAnalytics]);

  // Cleanup expired cache items periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      
      // Cleanup analytics cache
      Array.from(analyticsCache.current.entries()).forEach(([key, item]) => {
        if (now > item.expiresAt) {
          analyticsCache.current.delete(key);
        }
      });
      
      // DEPRECATED: Overview model removed - cleanup overview cache
      Array.from(overviewCache.current.entries()).forEach(([key, item]) => {
        if (now > item.expiresAt) {
          overviewCache.current.delete(key);
        }
      });
      
      // Cleanup component data cache
      Array.from(componentDataCache.current.entries()).forEach(([key, item]) => {
        if (now > item.expiresAt) {
          componentDataCache.current.delete(key);
        }
      });
      
      // Cleanup chart data cache
      Array.from(chartDataCache.current.entries()).forEach(([key, item]) => {
        if (now > item.expiresAt) {
          chartDataCache.current.delete(key);
        }
      });
    }, 60000); // Cleanup every minute

    return () => clearInterval(cleanupInterval);
  }, []);

  return (
    <AnalyticsCacheContext.Provider
      value={{
        analytics,
        // DEPRECATED: Overview model removed - returns empty object
        overviewData,
        isLoadingAnalytics,
        // DEPRECATED: Overview model removed - always false
        isLoadingOverview,
        analyticsError,
        // DEPRECATED: Overview model removed
        overviewError,
        getAnalytics,
        fetchAnalyticsData,
        // DEPRECATED: Overview model removed - returns null
        getOverviewData,
        // DEPRECATED: Overview model removed - returns null
        fetchOverviewData,
        getRetentionData,
        fetchRetentionData,
        // DEPRECATED: Overview model removed
        preloadAllData,
        cacheComponentData,
        getCachedComponentData,
        cacheChartData,
        getCachedChartData,
        clearWebsiteCache,
        clearAllCaches,
        getCacheStats,
        getCachedAnalyticsOnly
      }}
    >
      {children}
    </AnalyticsCacheContext.Provider>
  );
};

// Hook to use analytics cache context
export const useAnalyticsCache = () => {
  const context = useContext(AnalyticsCacheContext);
  if (!context) {
    throw new Error('useAnalyticsCache must be used within an AnalyticsCacheProvider');
  }
  return context;
};
